/**
 * Reading an EngineeringModel's boundary groups.
 *
 * A boundary group is three separate things (model/schema.py says so, and the
 * split is load-bearing): a FlowLab UUID that is the only identity, authored
 * semantics that are the user's to write, and a binding that records which
 * geometry entities the group currently resolves to. This module keeps them
 * apart, because collapsing them is exactly the failure the schema was built to
 * prevent -- a label is not a region, and a tag is not an identity.
 *
 * The tags matter to the viewer specifically: they are the values that appear
 * in the surface's `CellEntityIds` array, so a row here is what makes a
 * selection highlightable.
 */

/** BindingStatus, from the canonical schema. */
export const BOUND = 'bound';
export const STALE = 'stale';
export const AMBIGUOUS = 'ambiguous';
export const UNBOUND = 'unbound';

/** Statuses that a mesh run refuses. `assert_bindings_resolved` is the gate. */
const BLOCKING = new Set([STALE, AMBIGUOUS]);

/** Whether this model can be meshed at all, and what stops it if not. */
export function meshBlockers(model) {
  return (model?.boundary_groups || [])
    .filter((group) => BLOCKING.has(group.binding_status))
    .map((group) => ({
      id: group.id,
      label: group.label,
      status: group.binding_status,
      reason: group.binding_evidence?.reason || null,
    }));
}

/**
 * The entity tags a group resolves to, from the model alone.
 *
 * `binding.resolved` is the latest successful resolution, which is what the
 * mesher was handed, so it -- not `reference` -- is what the mesh carries.
 */
export function tagsOf(group) {
  const entities = group?.binding?.resolved?.entities || [];
  return entities.map((entity) => entity.tag).sort((left, right) => left - right);
}

/**
 * One row per boundary group, ready for the rail.
 *
 * `boundaryMap` is a Generation's own group-id -> tags mapping and wins where
 * it exists: it records what actually meshed, while the model's binding records
 * what the model currently resolves to, and those differ the moment the model
 * is re-bound after a Generation was built. Reading the Generation's map is
 * what keeps a highlight pointing at the mesh on screen.
 */
export function boundaryRows(model, boundaryMap = null) {
  return (model?.boundary_groups || []).map((group) => {
    const mapped = boundaryMap ? boundaryMap[group.id] : undefined;
    const tags = mapped ? [...mapped].sort((left, right) => left - right) : tagsOf(group);
    return {
      id: group.id,
      label: group.label,
      role: group.role,
      status: group.binding_status || UNBOUND,
      blocking: BLOCKING.has(group.binding_status),
      // From the Generation when one is loaded, so the tags describe the mesh
      // being displayed rather than the model's current state.
      fromGeneration: Boolean(mapped),
      tags,
      reason: group.binding_evidence?.reason || null,
    };
  });
}

/**
 * Group the rows by role, for a rail that reads as engineering rather than as
 * a flat list. Roles are open slugs, so the grouping is discovered from the
 * data and nothing here knows what a role may be.
 */
export function byRole(rows) {
  const groups = new Map();
  for (const row of rows) {
    if (!groups.has(row.role)) groups.set(row.role, []);
    groups.get(row.role).push(row);
  }
  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([role, members]) => ({ role, rows: members }));
}
